import { useState } from 'react'
import { MoreVertical, Play, X, RefreshCw, Download, Trash2, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useParseDocuments, useDeleteDocuments } from '@/hooks/useDocuments'
import knowledgeApi from '@/lib/knowledge-api'
import { MetadataDialog } from './MetadataDialog'

interface DocumentActionsProps {
  kbId: string
  ragflowDocumentId: string | null
  documentName: string
  status: string
  metadata?: Record<string, any>
}

export function DocumentActions({ kbId, ragflowDocumentId, documentName, status, metadata }: DocumentActionsProps) {
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false)
  const parseMutation = useParseDocuments(kbId)
  const deleteMutation = useDeleteDocuments(kbId)

  const handleParse = async () => {
    if (!ragflowDocumentId) return
    try {
      await parseMutation.mutateAsync([ragflowDocumentId])
    } catch (error) {
      console.error('Parse failed:', error)
    }
  }

  const handleReparse = async () => {
    if (!ragflowDocumentId) return
    try {
      // Use parse endpoint with document_ids for reparse (RAGflow re-parses automatically)
      await parseMutation.mutateAsync([ragflowDocumentId])
    } catch (error) {
      console.error('Reparse failed:', error)
    }
  }

  const handleCancel = async () => {
    if (!ragflowDocumentId) return
    try {
      await knowledgeApi.post(
        `/knowledge/${kbId}/documents/cancel/`,
        { document_ids: [ragflowDocumentId] }
      )
    } catch (error) {
      console.error('Cancel failed:', error)
    }
  }

  const handleDownload = async () => {
    if (!ragflowDocumentId) return
    try {
      const response = await knowledgeApi.get(
        `/knowledge/${kbId}/documents/${ragflowDocumentId}/download/`,
        {
          responseType: 'blob',
        }
      )
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', documentName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const handleDelete = async () => {
    if (!ragflowDocumentId) return
    if (!confirm(`Are you sure you want to delete "${documentName}"?`)) {
      return
    }
    
    try {
      await deleteMutation.mutateAsync([ragflowDocumentId])
    } catch (error) {
      console.error('Delete failed:', error)
    }
  }

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Context-aware actions based on status */}
        {(status === 'UNSTART' || status === 'FAIL') && (
          <DropdownMenuItem onClick={handleParse}>
            <Play className="h-4 w-4 mr-2" />
            Parse
          </DropdownMenuItem>
        )}
        
        {status === 'RUNNING' && (
          <DropdownMenuItem onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel Parsing
          </DropdownMenuItem>
        )}
        
        {status === 'DONE' && (
          <DropdownMenuItem onClick={handleReparse}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-parse
          </DropdownMenuItem>
        )}
        
        {/* Always available actions */}
        {(status === 'UNSTART' || status === 'FAIL' || status === 'RUNNING' || status === 'DONE') && (
          <DropdownMenuSeparator />
        )}
        
        <DropdownMenuItem onClick={handleDownload}>
          <Download className="h-4 w-4 mr-2" />
          Download
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => setMetadataDialogOpen(true)}>
          <Tag className="h-4 w-4 mr-2" />
          Metadata
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleDelete} className="text-destructive">
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    
    {/* Metadata Dialog */}
    {ragflowDocumentId && (
      <MetadataDialog
        open={metadataDialogOpen}
        onOpenChange={setMetadataDialogOpen}
        kbId={kbId}
        ragflowDocumentId={ragflowDocumentId}
        documentName={documentName}
        initialMetadata={metadata}
      />
    )}
  </>
  )
}
